import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { applyMeta } from "../lib/meta";
import { Loader2, MapPin, PoundSterling, Globe, Instagram, Music, Ticket } from "lucide-react";

export default function ArtistPublic() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/artists/${slug}`)
      .then(({ data }) => { setA(data); applyMeta({ title: `${data.name} — TicketsPro`, description: data.bio || `Book ${data.name} for your event.`, image: data.image_url }); })
      .catch(() => setError("Artist not found"));
  }, [slug]);

  if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-[#5a6b80]">{error}<Link to="/events" className="gp-btn-teal rounded-full px-6 py-2.5 font-medium">Browse events</Link></div>;
  if (!a) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={38} /></div>;

  const insta = a.instagram ? a.instagram.replace(/^@/, "") : "";
  return (
    <div className="min-h-screen bg-[#0f1830]" data-testid="artist-public-page">
      <div className="relative">
        <div className="h-56 sm:h-72 w-full overflow-hidden bg-[#16243b]">
          {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover opacity-40" /> : null}
        </div>
        <div className="max-w-[860px] mx-auto px-6 -mt-24 relative">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-40 h-40 rounded-2xl overflow-hidden bg-[#2ec9c9]/20 border-4 border-[#0f1830] shrink-0 flex items-center justify-center">
              {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" /> : <Music className="text-[#2ec9c9]" size={42} />}
            </div>
            <div className="pt-3 sm:pt-24">
              <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="artist-public-name">{a.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#9fb0c8]">
                {a.city && <span className="flex items-center gap-1.5"><MapPin size={15} className="text-[#2ec9c9]" /> {a.city}</span>}
                {a.base_fee != null && <span className="flex items-center gap-1.5"><PoundSterling size={15} className="text-[#2ec9c9]" /> From £{Number(a.base_fee).toFixed(0)}{a.fee_note ? ` · ${a.fee_note}` : ""}</span>}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-8 pb-16">
            <div className="md:col-span-2 space-y-6">
              {(a.genres || []).length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="artist-public-genres">
                  {a.genres.map((g) => <span key={g} className="text-xs font-medium rounded-full px-3 py-1 bg-[#8b5cf6]/20 text-[#c4b5fd]">{g}</span>)}
                </div>
              )}
              {a.bio && <p className="text-[#c7d2e5] leading-relaxed whitespace-pre-line" data-testid="artist-public-bio">{a.bio}</p>}
              <div className="flex flex-wrap gap-3">
                {a.website && <a href={a.website.startsWith("http") ? a.website : `https://${a.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#2ec9c9] hover:underline"><Globe size={15} /> Website</a>}
                {insta && <a href={`https://instagram.com/${insta}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#2ec9c9] hover:underline"><Instagram size={15} /> @{insta}</a>}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-5 h-fit">
              <p className="text-white font-semibold flex items-center gap-2"><Ticket size={17} className="text-[#2ec9c9]" /> Book {a.name}</p>
              <p className="text-sm text-[#9fb0c8] mt-2">Venues & promoters can send a booking enquiry directly through TicketsPro.</p>
              <Link to="/login" data-testid="artist-public-enquire" className="mt-4 block text-center gp-btn-teal rounded-full py-2.5 font-medium">Log in to enquire</Link>
              <p className="text-xs text-[#6b7c96] mt-3 text-center">Not on TicketsPro? <Link to="/signup" className="text-[#2ec9c9] hover:underline">Create an organiser account</Link></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
